import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useParams} from 'react-router';
import {Alert, CircularProgress, Divider, Stack, Typography} from '@mui/material';
import {useReportStore} from '../../../useReportStore';
import {getIcrByNumber as getIcrByNumberTest} from '../../../../collection-requests/_test_data/ICRequests';
import {getIcrByNumber as getIcrByNumberFS, resolveReportLinkedIcrNumber} from '../../../../../firebase/firestore/icr';
import {analyzeReportCoverageV2} from '../../../../../utils/api/aiCitationAnalysis';
import useYDocStore from '../../../../../stores/useYDocStore';
import TopicGroup from './TopicGroup';
import {
    buildDraftCoveragePayload,
    collectLiveClaims,
    draftRequirementsToTopicItems,
} from '../coverage/collectDraftCoveragePayload';

const DEBOUNCE_MS = 1500;

/**
 * ICR coverage analysis for AI Assistant — requirement-driven v2 (DU7).
 */
const IcrCoverageAnalysis = () => {
    const {reportId} = useParams();
    const icrNumber = useReportStore((state) => resolveReportLinkedIcrNumber({
        id: state.reportId,
        meta: state.meta,
    }));
    const editorView = useReportStore(state => state.editorView);
    const ydoc = useYDocStore(state => state.ydoc);
    const yReferenceMarks = useYDocStore(state => state.yReferenceMarks);

    const [icr, setIcr] = useState(null);
    const [loading, setLoading] = useState(false);
    const [analysisLoading, setAnalysisLoading] = useState(false);
    const [analysisError, setAnalysisError] = useState(null);
    const [analysisResult, setAnalysisResult] = useState(null);
    const abortRef = useRef(null);
    const debounceRef = useRef(null);

    const decoded = icrNumber ? decodeURIComponent(icrNumber) : undefined;

    const liveClaimCount = useMemo(
        () => collectLiveClaims(yReferenceMarks, reportId).length,
        [yReferenceMarks, reportId],
    );
    const claimsAnalyzed = analysisResult?.claimsAnalyzed ?? liveClaimCount;

    const analysisFingerprint = useMemo(() => {
        if (!editorView?.state?.doc) {
            return null;
        }
        const payload = buildDraftCoveragePayload({editorView, ydoc, yReferenceMarks, reportId});
        return JSON.stringify({
            contentHash: payload.contentHash,
            claims: payload.liveClaims.length,
        });
    }, [editorView, ydoc, yReferenceMarks, reportId]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            if (!decoded) {
                setIcr(null);
                return;
            }
            setLoading(true);
            try {
                const fsResult = await getIcrByNumberFS(decoded);
                if (!cancelled) {
                    setIcr(fsResult || getIcrByNumberTest(decoded));
                }
            } catch (e) {
                if (!cancelled) setIcr(getIcrByNumberTest(decoded));
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [decoded]);

    useEffect(() => {
        if (!icr || !reportId || !editorView || !analysisFingerprint) {
            return undefined;
        }

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(async () => {
            if (abortRef.current) {
                abortRef.current.abort();
            }
            const controller = new AbortController();
            abortRef.current = controller;

            try {
                setAnalysisLoading(true);
                setAnalysisError(null);

                const payload = buildDraftCoveragePayload({editorView, ydoc, yReferenceMarks, reportId});
                if (!payload.content) {
                    throw new Error('Editor not ready; open the report to load content.');
                }

                const result = await analyzeReportCoverageV2(reportId, payload);
                if (!controller.signal.aborted) {
                    setAnalysisResult(result);
                }
            } catch (err) {
                if (!controller.signal.aborted) {
                    setAnalysisError(err?.message || 'Failed to run analysis');
                }
            } finally {
                if (!controller.signal.aborted) {
                    setAnalysisLoading(false);
                }
            }
        }, DEBOUNCE_MS);

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
            if (abortRef.current) {
                abortRef.current.abort();
            }
        };
    }, [icr, reportId, editorView, ydoc, yReferenceMarks, analysisFingerprint]);

    if (loading) {
        return (
            <Stack p={2} alignItems="center" spacing={2} boxSizing="border-box">
                <CircularProgress size={24}/>
                <Typography variant="body2" color="text.secondary">Loading ICR…</Typography>
            </Stack>
        );
    }

    if (!icr) {
        return (
            <Stack p={2} spacing={2}>
                <Typography variant="h6" fontWeight={600}>No ICR</Typography>
                <Typography variant="body2" color="text.secondary">
                    No information collection request found for this report.
                </Typography>
            </Stack>
        );
    }

    const coveredItems = draftRequirementsToTopicItems(analysisResult?.covered, icr);
    const needsMoreItems = draftRequirementsToTopicItems(analysisResult?.needsMoreCoverage, icr);

    return (
        <Stack p={2} spacing={2}>
            <Stack spacing={1}>
                <Typography variant="subtitle1" fontWeight={600}>{icr.subject}</Typography>
                <Typography variant="caption" color="text.secondary">
                    {icr.icrNumber} • {icr.dtg} • {icr.requestingOrganization}
                </Typography>
            </Stack>

            <Divider/>

            {analysisLoading && (
                <Stack alignItems="center" py={2} spacing={1}>
                    <CircularProgress size={24}/>
                    <Typography variant="caption" color="text.secondary">
                        Analyzing draft coverage…
                    </Typography>
                </Stack>
            )}

            {analysisError && (
                <Alert severity="error">{analysisError}</Alert>
            )}

            {analysisResult && !analysisLoading && !analysisError && (
                <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        {analysisResult.coveredCount}/{analysisResult.requirementCount} questions answered
                        in this draft ({claimsAnalyzed} claims analyzed).
                    </Typography>

                    <TopicGroup
                        title="Covered"
                        color="success"
                        items={coveredItems}
                        showEmpty
                    />

                    <TopicGroup
                        title="Needs More Coverage"
                        color="warning"
                        items={needsMoreItems}
                        suggestExperts
                        showEmpty
                    />
                </Stack>
            )}
        </Stack>
    );
};

export default IcrCoverageAnalysis;
